import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ObservationDetails } from '../apiAndObjects/objects/observationDetails';
import { ApiService } from '../apiAndObjects/api/api.service';
import { SelectableObservationSummary } from '../apiAndObjects/objects/selectableObservationSummary';
import { ObservationSummary } from '../apiAndObjects/objects/observationSummary';
import { AddObservationParams } from '../apiAndObjects/api/observations/addObservation';

@Injectable()
export class ObservationsService {
  private searchResultsSrc = new BehaviorSubject<Array<SelectableObservationSummary>>([]);

  private lastFilterParamObject = new BehaviorSubject<object>(null);

  constructor(
    private apiService: ApiService,
  ) {
    // do an initial un-filtered search
    this.search();
  }

  get searchResults(): Array<SelectableObservationSummary> {
    return this.searchResultsSrc.value;
  }
  public watchSearchResults(): Observable<Array<SelectableObservationSummary>> {
    return this.searchResultsSrc.asObservable();
  }

  public search(parametersObject?: object): Promise<Array<SelectableObservationSummary>> {
    return this.apiService.endpoints.observations.get.call({ filterParametersObject: parametersObject })
      .then((results: Array<ObservationSummary>) => {
        const selectedableSummaries = results.map(sum => new SelectableObservationSummary(sum));
        // cache filter param object
        this.lastFilterParamObject.next(parametersObject);

        // if unfiltered search
        if (null == parametersObject) {
          this.setSearchResults(selectedableSummaries);
        } else {
          this.setSearchResults(this.searchResultsSrc.value, selectedableSummaries);
        }
        return selectedableSummaries;
      });
  }

  private setSearchResults(
    allResults: Array<SelectableObservationSummary>,
    filteredResults?: Array<SelectableObservationSummary>,
  ): void {
    allResults.forEach((summary: SelectableObservationSummary) => {
      // no filter = nothing selected
      // filter = only filter results selected
      const selected = (null == filteredResults)
        ? false
        : (null != filteredResults.find((result: SelectableObservationSummary) => (result.id === summary.id)));
      summary.setSelected(selected);
    });
    this.searchResultsSrc.next(allResults);
  }

  public getDetails(_id: string): Promise<ObservationDetails> {
    return this.apiService.endpoints.observations.details.call({ id: _id });
  }

  public add(submission: AddObservationParams): Promise<void> {
    // console.debug('submission', submission);
    return this.apiService.endpoints.observations.add.call(submission);
  }

  public clearObservationSelectedFlags(): void {
    // only if anything currently selected
    if (null != this.searchResultsSrc.value.find(summary => summary.isSelected)) {
      this.setSearchResults(this.searchResultsSrc.value);
    }
  }

  public watchFilterParameters(): Observable<object> {
    return this.lastFilterParamObject.asObservable();
  }

  public searchForExport(useFilter: boolean): Promise<Array<object>> {
    const filter = (useFilter) ? this.lastFilterParamObject.getValue() : null;
    return this.apiService.endpoints.observations.forExport.call({ filterParametersObject: filter });
  }

}
